/**
 * Input Component - PouchPay Design System
 * Form inputs with consistent styling
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, icon, className, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-[var(--font-size-label)] font-[var(--font-weight-medium)] text-[var(--fg-2)] mb-[var(--space-s)]">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute left-[var(--space-l)] top-1/2 -translate-y-1/2 text-[var(--fg-3)]">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              'input w-full',
              icon && 'pl-[calc(var(--space-l)*2+24px)]',
              error && 'border-[var(--danger)] focus:ring-[var(--danger)]',
              className
            )}
            {...props}
          />
        </div>
        {error && (
          <p className="mt-[var(--space-s)] text-[var(--font-size-body-s)] text-[var(--danger)]">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-[var(--space-s)] text-[var(--font-size-body-s)] text-[var(--fg-3)]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-[var(--font-size-label)] font-[var(--font-weight-medium)] text-[var(--fg-2)] mb-[var(--space-s)]">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          className={cn(
            'input w-full min-h-[120px] resize-y',
            error && 'border-[var(--danger)] focus:ring-[var(--danger)]',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-[var(--space-s)] text-[var(--font-size-body-s)] text-[var(--danger)]">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-[var(--space-s)] text-[var(--font-size-body-s)] text-[var(--fg-3)]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Input, Textarea };
